import * as React from "react";
import {
    DataTable,
    DataTableHeader,
    DataTableHeaderColumn,
    DataTableBody,
    DataTableToolsRow,
    DataTableRow,
    DataTableRowColumn
} from "../DataTable";
import BaseComponent from "../BaseComponent";
import styles = require("./style.less");
import cn from "classnames";
import InternalRedirect from "../Navigation/InternalRedirect/InternalRedirect";
import TransitionAnimation from "components/TransitionAnimation/TransitionAnimation";
import OverflowMenu, {MenuItem} from "components/Menu/OverflowMenu";

interface SimpleDataTableProps<TData> {
    onToolsSection?: any;
    headerColumns: any[];
    headerColumnClassName?: string;
    rowColumnClassName?: string;
    tableClassName?: string;
    data: TData[];
    onEmpty?: React.ReactNode;
    match?: any;
    onRow(item: TData): React.ReactNode[];
    // Optional full-width row rendered beneath an item's main row (spanning all columns). Use for
    // detail content too wide for a column, e.g. a message body. Return null/undefined for no detail.
    onRowDetail?(item: TData): React.ReactNode;
    onRowOverflowMenu?(item: TData): MenuItem[];
    onRowRedirectUrl?(item: TData): string;
    onRowClick?(item: TData): void;
}

interface SimpleDataTableState {
    redirectTo?: string;
}

export default abstract class SimpleDataTable<TData> extends BaseComponent<SimpleDataTableProps<TData>, SimpleDataTableState> {
    constructor(props: SimpleDataTableProps<TData>) {
        super(props);
        this.state = {};
    }

    render() {
        if (this.state.redirectTo) {
            return <InternalRedirect to={this.state.redirectTo} push={true} />;
        }

        const {
            onToolsSection,
            data,
            headerColumns,
            headerColumnClassName,
            rowColumnClassName,
            onRow,
            onEmpty
        } = this.props;

        if (!data) {
            return null;
        }

        return <TransitionAnimation>
            <DataTable>
                <DataTableHeader>
                    {onToolsSection ?
                        <DataTableToolsRow>
                            <DataTableHeaderColumn
                            colSpan={headerColumns.length}>
                            {onToolsSection()}
                            </DataTableHeaderColumn>
                        </DataTableToolsRow>
                        : null
                    }
                    {data.length > 0
                    ? this.buildTableHeader(headerColumns, headerColumnClassName)
                    : null}
                </DataTableHeader>
                <DataTableBody>
                {data.length > 0
                ? data.map((item, index) => {
                    return this.buildTableRow(item, index, onRow(item), rowColumnClassName);
                })
                : <DataTableRow displayNoBorder={true}>
                    <DataTableRowColumn>
                        {onEmpty}
                    </DataTableRowColumn>
                </DataTableRow>
                }
                </DataTableBody>
            </DataTable>
        </TransitionAnimation>;
    }

    private buildTableHeader(headerColumns: any[], className: string) {
        return (
            <DataTableRow>
                {headerColumns.map((col, index) => {
                    const props: any = {
                        key: index
                    };

                    if (className) {
                        props["className"] = className;
                    }

                    return (
                        <DataTableHeaderColumn {...props}>
                            {col}
                        </DataTableHeaderColumn>
                    );
                })}
                {this.props.onRowOverflowMenu && <DataTableHeaderColumn className={styles.overflowMenuColumn} />}
            </DataTableRow>
        );
    }

    private buildTableRow(item: TData, index: number, rowColumns: any, className: string) {
        // Only some tables include a redirect, so only show cursor if necessary.
        const redirectUrl = this.getNavigationUrl(this.props, item);
        const linkClassName = redirectUrl ? styles.linkableItem : null;
        const detail = this.props.onRowDetail ? this.props.onRowDetail(item) : null;
        const hasDetail = detail !== null && detail !== undefined && detail !== false;
        const totalColumns = this.props.headerColumns.length + (this.props.onRowOverflowMenu ? 1 : 0);
        return (
            <React.Fragment key={index}>
            <DataTableRow onClick={() => this.onRowClicked(item)}>
                {rowColumns.map((col: any, idx: number) => {
                    const props: any = {
                        key: idx
                    };
                    if (className) {
                        props["className"] = cn(className, props["className"]);
                    }
                    if (linkClassName) {
                        props["className"] = cn(linkClassName, props["className"]);
                    }
                    return (
                        <DataTableRowColumn {...props}>
                            {col}
                        </DataTableRowColumn>
                    );
                })}
                {this.props.onRowOverflowMenu && <DataTableRowColumn className={styles.overflowMenuColumn}>
                    <OverflowMenu menuItems={this.props.onRowOverflowMenu(item)} />
                </DataTableRowColumn>}
            </DataTableRow>
            {/* Detail row has no top border so it groups with its own row above; the next item's
                row keeps its border, which then separates one item (row + detail) from the next. */}
            {hasDetail && <DataTableRow displayNoBorder={true}>
                <DataTableRowColumn colSpan={totalColumns}>
                    {detail}
                </DataTableRowColumn>
            </DataTableRow>}
            </React.Fragment>
        );
    }

    private onRowClicked(item: TData) {
        // A drag-to-select-text gesture inside the row also fires a click event on the
        // <tr> (the browser targets the common ancestor of mousedown and mouseup), so
        // don't treat it as a row click while the user has text selected.
        const selection = window.getSelection();
        if (selection && !selection.isCollapsed) {
            return;
        }
        this.props.onRowClick ? this.props.onRowClick(item) : this.navigate(item);
    }

    private navigate(item: TData) {
        const redirectTo = this.getNavigationUrl(this.props, item);
        if (!redirectTo) {
            return;
        }
        this.setState({ redirectTo });
    }

    private getNavigationUrl<R>({ match, onRowRedirectUrl }: { match?: any, onRowRedirectUrl?(item: R): string }, item: R): string {
        if (onRowRedirectUrl) {
            return onRowRedirectUrl(item);
        } else if (match && (item as any).Id) {
            return `${match.url}/${(item as any).Id}`;
        }
        return null;
    }
}